import React, { useState } from 'react'
import {
    Box,
    Button,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

const useStyles = makeStyles((theme: Theme) => ({
    row: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.75),
        padding: theme.spacing(1.5, 0),
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    label: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        letterSpacing: 0.2,
    },
    value: {
        fontSize: '0.875rem',
        lineHeight: 1.55,
        whiteSpace: 'pre-wrap',
        overflowWrap: 'anywhere',
        color: theme.palette.text.primary,
    },
    empty: {
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
    },
    edit: {
        fontSize: '0.75rem',
        fontWeight: 600,
        minWidth: 0,
        padding: theme.spacing(0.25, 1),
    },
    editor: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
    },
    editorActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
    },
}))

type Props = {
    label: string
    value: string
    onChange: (value: string) => void
}

export const CommunityFactRow: React.FC<Props> = ({
    label,
    value,
    onChange,
}) => {
    const classes = useStyles()
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(value)

    const startEdit = () => {
        setDraft(value)
        setEditing(true)
    }

    const cancelEdit = () => {
        setDraft(value)
        setEditing(false)
    }

    const commitEdit = () => {
        if (draft !== value) {
            onChange(draft)
        }
        setEditing(false)
    }

    return (
        <Box className={classes.row}>
            <Box className={classes.header}>
                <Typography className={classes.label}>{label}</Typography>
                {!editing && (
                    <Button
                        className={classes.edit}
                        color="primary"
                        onClick={startEdit}
                    >
                        {value.trim() ? 'Edit' : 'Add'}
                    </Button>
                )}
            </Box>
            {editing ? (
                <Box className={classes.editor}>
                    <TextField
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        fullWidth
                        multiline
                        rows={4}
                        variant="outlined"
                        size="small"
                        autoFocus
                    />
                    <Box className={classes.editorActions}>
                        <Button onClick={cancelEdit}>Cancel</Button>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={commitEdit}
                        >
                            Done
                        </Button>
                    </Box>
                </Box>
            ) : (
                <Typography
                    className={
                        value.trim()
                            ? classes.value
                            : `${classes.value} ${classes.empty}`
                    }
                >
                    {value.trim() || 'Not set'}
                </Typography>
            )}
        </Box>
    )
}
